<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12">
          <v-card class="px-7 mt-4 pt-7">
            <v-row>
              <v-col cols="12">
                <SimplePageHeader
                  v-if="!isEdit"
                  title="Add Client"
                ></SimplePageHeader>
                <SimplePageHeader
                  title="Edit Client"
                  v-else
                >
                  <template v-slot:right-buttons>
                    <v-switch
                      v-model="active"
                      label="Active"
                      class="float-end"
                      :readonly="isReadOnlyAccess"
                    ></v-switch>
                  </template>
                </SimplePageHeader>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-tabs v-model="tab">
                  <v-tab :key="CLIENT">
                    Client
                  </v-tab>
                  <v-tab :key="STAKEHOLDERS">
                    Stakeholders
                  </v-tab>
                  <v-tab :key="DEFAULT_EXPENSES">
                    Default Expenses
                  </v-tab>
                  <v-tab :key="WORKFLOW">
                    Workflow
                  </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab">
                  <v-tab-item :key="CLIENT">
                    <v-row class="pt-5">
                      <v-col cols="12" sm="6" md="3" xl="2">
                        <BusinessEntitiesDropDownList
                          v-model="businessEntityId"
                          label="Business Entity"
                          :isRequired="true"
                          :clearable="!isReadOnlyAccess"
                          :readonly="isReadOnlyAccess"
                          @businessEntityIdChange="handleBusinessEntityChange"
                        ></BusinessEntitiesDropDownList>
                      </v-col>
                      <v-col cols="12" sm="6" md="3" xl="2">
                        <v-text-field
                          label="Name"
                          v-model="name"
                          :rules="[Required('Client Name')]"
                          validate-on-blur
                          outlined
                          :readonly="isReadOnlyAccess"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="3" xl="2">
                        <InvoiceFrequenciesDropDownList
                          :id="invoiceFrequencyId"
                          :isRequired="true"
                          :read-only="isReadOnlyAccess"
                          @change="(newValue) => this.invoiceFrequencyId = newValue"
                        ></InvoiceFrequenciesDropDownList>
                      </v-col>
                      <v-col cols="12" sm="6" md="3" lg="3" xl="2">
                        <ThirdPartyPortalsDropDownList
                          :id="thirdPartyPortalId"
                          :isRequired="false"
                          :clearable="!isReadOnlyAccess"
                          :readonly="isReadOnlyAccess"
                          @ThirdPartyPortalIdChange="(newValue) => this.thirdPartyPortalId = newValue"
                        ></ThirdPartyPortalsDropDownList>
                      </v-col>
                      <v-col cols="12" sm="6" md="3" xl="2">
                        <FormattedNumberField
                          label="VMS Fee"
                          :value="vmsFee"
                          append-icon="percent"
                          :readonly="isReadOnlyAccess"
                          :clearable="true"
                          @valueChange="(newValue) => this.vmsFee = newValue"
                        ></FormattedNumberField>
                      </v-col>
                    </v-row>
                    <v-row class="pt-0 pb-5">
                      <v-col cols="12" class="pt-0">
                        <v-checkbox
                          v-model="useStaffLynxForTimeEntering"
                          label="Use StaffLynx for Time Entry"
                          :readonly="isReadOnlyAccess"
                          :disabled="clientOfRecord"
                          :hint="clientOfRecord ? 'Client of Record already excludes this client\'s assignments from time entry.' : ''"
                          :persistent-hint="clientOfRecord"
                        />
                        <v-checkbox
                          v-model="clientOfRecord"
                          label="Client of Record"
                          hint="Client supplies its own hours; overtime is not calculated automatically and consultants do not enter time."
                          persistent-hint
                          :readonly="isReadOnlyAccess"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="canMapQuickBooks" class="pt-0 pb-5">
                      <v-col cols="12" class="pt-0">
                        <ClientQuickBooksMapping
                          :businessEntityId="businessEntityId"
                          :customerId.sync="quickBooksCustomerId"
                          :itemId.sync="quickBooksTimesheetItemId"
                          :postAutomatically.sync="postToQuickBooksAutomatically"
                        />
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item :key="STAKEHOLDERS">
                    <ClientStakeHolderCollector
                      class="py-5"
                      label="Send Invoices To: (Client Stakeholder)"
                      :recipientsList="clientStakeholderRecipients"
                      :readonly="isReadOnlyAccess"
                      :disabled="false"
                      @nameChange="(id, newValue) => this.clientStakeholderRecipients[id]['stakeholderName'] = newValue"
                      @emailChange="(id, newValue) => this.clientStakeholderRecipients[id]['emailAddress'] = newValue"
                      @notesChange="(id, newValue) => this.clientStakeholderRecipients[id]['notes'] = newValue"
                      @deleteRecipient="(id) => this.clientStakeholderRecipients.splice(id, 1)"
                      @addRecipient="() => this.clientStakeholderRecipients.push({stakeholderName: '', emailAddress: '', notes: ''})"
                    ></ClientStakeHolderCollector>
                  </v-tab-item>
                  <v-tab-item :key="DEFAULT_EXPENSES">
                    <v-row class="my-5">
                      <v-col cols="12">
                        <ExpensesCard
                          class="px-2"
                          cardTitle="Contractor"
                          :expensesList="contractorExpenses"
                          :readonly="isReadOnlyAccess"
                          :showActive="true"
                          :showExpenseAmountType="true"
                          @expensesListChange="(newValue) => this.contractorExpenses = newValue"
                        ></ExpensesCard>
                      </v-col>
                    </v-row>
                    <v-divider></v-divider>
                    <v-row class="my-5">
                      <v-col cols="12">
                        <ExpensesCard
                          class="px-2"
                          cardTitle="Perm. Placements"
                          :expensesList="directPlacementsExpenses"
                          :readonly="isReadOnlyAccess"
                          :showActive="true"
                          @expensesListChange="(newValue) => this.directPlacementsExpenses = newValue"
                        ></ExpensesCard>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item :key="WORKFLOW">
                    <ClientWorkflowTab
                      :clientId="id"
                      :isReadOnly="isReadOnlyAccess"
                      @clone-pending="pendingSourceTemplateId = $event"
                    />
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="end" v-if="isReadOnlyAccess">
        <v-col cols="6" md="3" class="pl-2">
          <v-btn
            block
            outlined
            depressed
            @click="cancel"
          >
            Back
          </v-btn>
        </v-col>
      </v-row>
      <ButtonsSubmitForm
        v-if="!isReadOnlyAccess"
        :isFormValid="isFormValid"
        @save="handleSaveClient"
        @cancel="cancel"
      >
      </ButtonsSubmitForm>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { clientsRoute } from '@/lib/constants/routes'
import { ButtonsSubmitForm } from '@/components/buttons'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { SimplePageHeader } from '@/components/pageComponents'
import { InvoiceFrequenciesDropDownList } from '@/components/invoiceFrequencies'
import Required from '@/lib/formValidations/required'
import ThirdPartyPortalsDropDownList from '@/components/clients/ThirdPartyPortalsDropDownList'
import ClientStakeHolderCollector from '@/components/emails/ClientStakeHolderCollector'
import { ExpensesCard } from '@/components/expenses'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { FormattedNumberField } from '@/components/shared/textFields'
import ClientWorkflowTab from './ClientWorkflowTab'
import { ClientQuickBooksMapping } from '@/components/quickBooks'

export default {
  components: {
    ButtonsSubmitForm,
    BusinessEntitiesDropDownList,
    SimplePageHeader,
    InvoiceFrequenciesDropDownList,
    ThirdPartyPortalsDropDownList,
    ClientStakeHolderCollector,
    ExpensesCard,
    FormattedNumberField,
    ClientWorkflowTab,
    ClientQuickBooksMapping
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.previousRoute = from
    })
  },
  data: () => ({
    isFormValid: false,
    previousRoute: null,
    tab: null,
    CLIENT: 1,
    STAKEHOLDERS: 2,
    DEFAULT_EXPENSES: 3,
    WORKFLOW: 4,
    pendingSourceTemplateId: null,
    loadedQuickBooksMapping: null
  }),
  computed: {
    ...mapFields('client', [
      'businessEntityId',
      'name',
      'active',
      'invoiceFrequencyId',
      'thirdPartyPortalId',
      'clientStakeholderRecipients',
      'vmsFee',
      'contractorExpenses',
      'directPlacementsExpenses',
      'zohoClientId',
      'useStaffLynxForTimeEntering',
      'clientOfRecord',
      'quickBooksCustomerId',
      'quickBooksTimesheetItemId',
      'postToQuickBooksAutomatically'
    ]),
    id: {
      get () {
        return this.$route.params.id || null
      }
    },
    isEdit: {
      get () {
        return !!this.id
      }
    },
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    canMapQuickBooks () {
      return !!this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN] })
    }
  },
  methods: {
    getCurrentUser,
    userHasRoles,
    Required,
    ...mapActions('client', [
      'getClient',
      'saveClient',
      'clearClient'
    ]),
    ...mapActions('workflowTemplate', ['copyWorkflowTemplate']),
    loadClientData () {
      return this.getClient({ id: this.id })
        .then(client => {
          if (!client) return
          this.loadedQuickBooksMapping = {
            businessEntityId: client.businessEntityId,
            quickBooksCustomerId: client.quickBooksCustomerId,
            quickBooksTimesheetItemId: client.quickBooksTimesheetItemId,
            postToQuickBooksAutomatically: client.postToQuickBooksAutomatically
          }
        })
    },
    async handleSaveClient () {
      if (!this.$refs.form.validate()) return
      const clientName = this.name
      const wasNew = !this.id

      const response = await this.saveClient()
      if (!response || !response.data) return

      if (this.pendingSourceTemplateId) {
        const clientId = wasNew ? response.data.id : parseInt(this.id)
        await this.copyWorkflowTemplate({
          sourceTemplateId: this.pendingSourceTemplateId,
          name: `${clientName} Workflow`,
          clientId
        })
      }

      if (this.previousRoute && this.previousRoute.name === clientsRoute.name) {
        this.$router.back()
        return
      }
      this.$router.push({ name: clientsRoute.name })
    },
    handleBusinessEntityChange (businessEntityId) {
      const loaded = this.loadedQuickBooksMapping
      if (loaded && loaded.businessEntityId === businessEntityId) {
        this.quickBooksCustomerId = loaded.quickBooksCustomerId
        this.quickBooksTimesheetItemId = loaded.quickBooksTimesheetItemId
        this.postToQuickBooksAutomatically = loaded.postToQuickBooksAutomatically
        return
      }
      this.quickBooksCustomerId = ''
      this.quickBooksTimesheetItemId = ''
      this.postToQuickBooksAutomatically = false
    },
    handleInvoiceFrequencyChange (value) {
      this.invoiceFrequencyId = value
    },
    cancel () {
      if (this.previousRoute && this.previousRoute.name === clientsRoute.name) {
        this.$router.back()
        return
      }
      this.$router.push({ name: clientsRoute.name })
    }
  },
  mounted () {
    this.$refs.form.resetValidation()
    if (this.isEdit) {
      this.loadClientData()
    } else {
      this.clearClient()
    }
  }
}
</script>
